import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import Popover from './Popover';

/**
 * SearchableSelect — Dropdown's sibling for long option lists. Same control
 * shell, but the menu opens with a type-to-filter search field. Value is
 * nullable: allowClear renders a "No selection" row.
 */
export interface SearchableSelectOption {
  value: string;
  label: string;
  hint?: string;
}

export interface SearchableSelectProps {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  clearLabel?: string;
  options: SearchableSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  allowClear?: boolean;
  size?: 'sm' | 'md';
  /** The menu is the trigger's width, or this many px when that is wider (room for hints). */
  menuMinWidth?: number;
  /** Open the menu as soon as it mounts, for a picker revealed by a click elsewhere. */
  defaultOpen?: boolean;
  style?: React.CSSProperties;
}

export default function SearchableSelect({
  label,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  clearLabel = 'No selection',
  options = [],
  value,
  onChange,
  allowClear = false,
  size = 'md',
  menuMinWidth,
  defaultOpen = false,
  style,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // After mount: the menu anchors to the trigger, which doesn't exist on the first render.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = options.find((o) => o.value === value);
  const filtered = query
    ? options.filter((o) => `${o.label} ${o.hint ?? ''}`.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (open) {
      setQuery('');
      // Popover portals in on the same tick; focus once it exists
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const pick = (v: string | null) => {
    onChange(v);
    setOpen(false);
  };

  const onSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0) pick(filtered[0].value);
    }
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      {label ? <div className="t-eyebrow" style={{ marginBottom: '7px' }}>{label}</div> : null}
      <button
        ref={buttonRef}
        type="button"
        className="pressable"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          width: '100%',
          minWidth: '120px',
          height: size === 'sm' ? 'var(--control-sm)' : 'var(--control-md)',
          padding: '0 11px 0 12px',
          background: 'var(--surface-raised)',
          border: '1px solid ' + (open ? 'var(--accent)' : 'var(--border-hairline)'),
          borderRadius: 'var(--radius-md)',
          color: selected ? 'var(--text-body)' : 'var(--text-faint)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--text-sm)',
          cursor: 'pointer',
          textAlign: 'left',
          whiteSpace: 'nowrap',
          transition: 'border-color var(--dur-base) var(--ease-out), background var(--dur-base) var(--ease-out)',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selected ? selected.label : placeholder}
        </span>
        <Icon
          name="chevron-down"
          size={12}
          style={{
            color: 'var(--text-faint)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-base) var(--ease-out)',
          }}
        />
      </button>

      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        matchWidth
        minWidth={menuMinWidth}
        role="listbox"
        style={{ maxHeight: '300px', display: 'flex', flexDirection: 'column' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            padding: '6px 8px',
            borderBottom: '1px solid var(--border-hairline)',
            marginBottom: '3px',
          }}
        >
          <Icon name="search" size={12} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKey}
            placeholder={searchPlaceholder}
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-body)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-sm)',
            }}
          />
        </div>

        <div style={{ overflowY: 'auto' }}>
          {allowClear && !query ? (
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              className="hover-wash pressable"
              onClick={() => pick(null)}
              style={{
                display: 'block',
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                textAlign: 'left',
                background: 'transparent',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontStyle: 'italic',
                color: 'var(--text-faint)',
              }}
            >
              {clearLabel}
            </button>
          ) : null}

          {filtered.length === 0 ? (
            <div
              style={{
                padding: '10px',
                textAlign: 'center',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-faint)',
              }}
            >
              No matches
            </div>
          ) : (
            filtered.map((o) => {
              const isSel = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  className="hover-wash pressable"
                  onClick={() => pick(o.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    width: '100%',
                    padding: '7px 10px',
                    border: 'none',
                    textAlign: 'left',
                    background: 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: (isSel ? 'var(--weight-medium)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                    color: 'var(--text-body)',
                  }}
                >
                  <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.label}
                  </span>
                  {o.hint ? (
                    // The hint gives way before the label does.
                    <span
                      title={o.hint}
                      style={{ flex: '0 1000 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
                    >
                      {o.hint}
                    </span>
                  ) : null}
                  {isSel ? <Icon name="check" size={12} style={{ color: 'var(--accent-text)', flexShrink: 0 }} /> : null}
                </button>
              );
            })
          )}
        </div>
      </Popover>
    </div>
  );
}
